// 🎯 USE DEBOUNCE HOOK - Hook React para debouncing
// Proporciona funcionalidad de debouncing para componentes

import { useCallback, useRef, useEffect, useState } from "react";
import { debounceService, DebounceService, type DebouncedFunction } from "@/services";

export interface UseDebounceOptions {
  delay: number;
  maxWait?: number;
  leading?: boolean;
  trailing?: boolean;
}

/**
 * Hook para debouncing de funciones
 */
export function useDebounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  options: Partial<UseDebounceOptions> = {}
): DebouncedFunction<T> {
  const debouncedRef = useRef<DebouncedFunction<T> | null>(null);

  useEffect(() => {
    debouncedRef.current = debounceService.debounce(func, delay, options);

    return () => {
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, [func, delay, options]);

  return useCallback(
    (...args: Parameters<T>) => {
      if (debouncedRef.current) {
        debouncedRef.current(...args);
      }
    },
    []
  ) as DebouncedFunction<T>;
}

/**
 * Hook para debouncing de API calls
 */
export function useDebounceApiCall<T extends (...args: any[]) => Promise<any>>(
  apiCall: T,
  delay: number = 300
): DebouncedFunction<T> {
  const debouncedRef = useRef<DebouncedFunction<T> | null>(null);

  useEffect(() => {
    debouncedRef.current = debounceService.debounceApiCall(apiCall, delay);

    return () => {
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, [apiCall, delay]);

  return useCallback(
    (...args: Parameters<T>) => {
      if (debouncedRef.current) {
        debouncedRef.current(...args);
      }
    },
    []
  ) as DebouncedFunction<T>;
}

/**
 * Hook para debouncing de interacciones de usuario
 */
export function useDebounceUserInteraction<T extends (...args: any[]) => any>(
  func: T,
  delay: number = 250
): DebouncedFunction<T> {
  const debouncedRef = useRef<DebouncedFunction<T> | null>(null);

  useEffect(() => {
    debouncedRef.current = debounceService.debounceUserInteraction(func, delay);

    return () => {
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, [func, delay]);

  return useCallback(
    (...args: Parameters<T>) => {
      if (debouncedRef.current) {
        debouncedRef.current(...args);
      }
    },
    []
  ) as DebouncedFunction<T>;
}

/**
 * Hook para debouncing de búsquedas
 */
export function useDebounceSearch<T extends (...args: any[]) => any>(
  searchFunc: T,
  delay: number = 500
): DebouncedFunction<T> {
  const debouncedRef = useRef<DebouncedFunction<T> | null>(null);

  useEffect(() => {
    debouncedRef.current = debounceService.debounceSearch(searchFunc, delay);

    return () => {
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, [searchFunc, delay]);

  return useCallback(
    (...args: Parameters<T>) => {
      if (debouncedRef.current) {
        debouncedRef.current(...args);
      }
    },
    []
  ) as DebouncedFunction<T>;
}

/**
 * Hook para obtener estadísticas de debouncing
 */
export function useDebounceStats() {
  const [stats, setStats] = useState(() => {
    const service = debounceService as InstanceType<typeof DebounceService>;
    return service.getStats();
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const service = debounceService as InstanceType<typeof DebounceService>;
      setStats(service.getStats());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return stats;
}

/**
 * Hook para debounce de valores (no funciones)
 *
 * @description Útil para debounce de inputs de búsqueda
 * donde quieres retrasar el valor, no la función
 *
 * @param value Valor a hacer debounce
 * @param delay Tiempo de espera en milisegundos (default: 500ms)
 * @returns Valor con debounce aplicado
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounceValue(searchTerm, 300);
 *
 * // Solo filtra cuando el usuario para de escribir
 * const filtered = useMemo(() =>
 *   products.filter(p => p.name.includes(debouncedSearch)),
 *   [products, debouncedSearch]
 * );
 * ```
 */
export function useDebounceValue<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}